import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from './Card';
import {
  Clock,
  Eye,
  CheckCircle2,
  Flame,
  ChevronDown,
  AlertOctagon,
  AlertCircle,
  Ban,
  CalendarDays,
  History,
} from 'lucide-react';
import { TONES, avatarStyle, initials } from '../theme';
import { useToast } from '../../../Utils/toastContext';

// showToast is already imported/available in your project — this component
// uses it if it's in scope, otherwise it just logs (no extra import needed).

/* Review button targets `/review-kyc/${customer_user_id}` and mirrors the
   redirect-after-login pattern:
   sessionStorage.setItem('redirectAfterLogin', JSON.stringify({ path, state })) */

const PRIORITY = {
  HIGH: {
    chip: 'text-[#DC2626] bg-[#FEF2F2] ring-1 ring-[#FECACA]',
    accent: '#EF4444',
    icon: AlertOctagon,
  },
  MEDIUM: {
    chip: 'text-[#D97706] bg-[#FFFBEB] ring-1 ring-[#FDE68A]',
    accent: '#F59E0B',
    icon: AlertCircle,
  },
  LOW: {
    chip: 'text-[#059669] bg-[#ECFDF5] ring-1 ring-[#A7F3D0]',
    accent: '#10B981',
    icon: CheckCircle2,
  },
  BLOCKED: {
    chip: 'text-white bg-slate-800',
    accent: '#334155',
    icon: Ban,
  },
};

const DUE_TEXT = {
  over: 'text-[#DC2626]',
  soon: 'text-[#D97706]',
  blocked: 'text-slate-400',
  normal: 'text-[#059669]', // added so fresh cases (< 6h) still have a colour
};

const SLA_BAR = {
  over: 'bg-[#EF4444]',
  soon: 'bg-[#F59E0B]',
  blocked: 'bg-slate-500',
  normal: 'bg-[#10B981]',
};

const SLA_TARGET_HOURS = 24; // waiting_hours that fills the SLA micro-bar

/* Format a timestamp as dd/mm/yyyy hh:mm (24h).
   Falls back to the raw value when it can't be parsed as a date. */
const formatSubmitted = (value) => {
  if (value == null) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* "8h ago" style label for last_activity_at (API gives waiting_label /
   submitted_label but no last-activity label). */
const formatRelative = (value) => {
  if (value == null) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatSubmitted(d);
};

const prettyStatus = (label) =>
  String(label || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getRoleName = () => {
  try {
    const raw = localStorage.getItem('roleName');
    if (!raw) return '';
    try {
      return String(JSON.parse(raw)).toLowerCase();
    } catch {
      return String(raw).toLowerCase();
    }
  } catch {
    return '';
  }
};

/* ---------------- API response → UI model (fields used by this card) ---------------- */
const mapAttentionItem = (raw) => {
  const hours =
    typeof raw?.waiting_hours === 'number' && Number.isFinite(raw.waiting_hours)
      ? raw.waiting_hours
      : 0;
  const blocked = !!raw?.is_blocked;
  const severity = String(raw?.severity || '').toLowerCase();

  const priority = blocked
    ? 'BLOCKED'
    : severity === 'critical' || severity === 'high' || hours >= 24
      ? 'HIGH'
      : severity === 'medium' || hours >= 6
        ? 'MEDIUM'
        : 'LOW';

  const dueTone = blocked
    ? 'blocked'
    : hours >= 24
      ? 'over'
      : hours >= 6
        ? 'soon'
        : 'normal';

  const sla = blocked
    ? 100
    : Math.max(6, Math.min(100, Math.round((hours / SLA_TARGET_HOURS) * 100)));

  const caseRef = raw?.case_reference || '';
  const clientId = raw?.client_id || '';

  return {
    id:
      raw?.relationship_id ?? raw?.customer_user_id ?? `${clientId}-${caseRef}`,
    customerUserId: raw?.customer_user_id ?? null,
    relationshipId: raw?.relationship_id ?? null,
    name: raw?.full_name || 'Unknown customer',
    // client id, plus case reference when it differs from the client id
    sub:
      caseRef && caseRef !== clientId
        ? `${clientId} · ${caseRef}`
        : clientId || caseRef || '—',
    // blocked cases surface the reason, otherwise the API message
    note:
      (blocked && raw?.block_reason) ||
      raw?.message ||
      prettyStatus(raw?.status?.label),
    due: raw?.waiting_label ?? (hours ? `${Math.round(hours)}h` : '—'),
    dueTone,
    sla,
    priority,
    opened: raw?.submitted_at ?? raw?.waiting_since ?? null,
    last: formatRelative(raw?.last_activity_at ?? raw?.submitted_at),
    actionLabel: raw?.action?.label || 'Review',
    actionType: raw?.action?.type || 'review',
    caseType: raw?.case_type || '',
    statusLabel: prettyStatus(raw?.status?.label),
    raw,
  };
};

export default function AttentionCard({ limit = 5 }) {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();

  const [expanded, setExpanded] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getAttention = useCallback(async () => {
    setLoading(true);
    const url = `/api/v1/agency/dashboard/attention?limit=${limit}`;
    try {
      const response = await GetAPIcalls(url);
      const payload = response?.data ?? response ?? {};
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.items)
          ? payload.items
          : [];
      setItems(list.map(mapAttentionItem));
    } catch (err) {
      setItems([]);
      showToast(
        typeof err?.message === 'string' && err.message
          ? err.message
          : 'Could not load cases needing attention',
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [limit]);
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
          status: response.status,
        }));

        // Ensure errorData.message is extracted properly
        const errorMessage =
          typeof errorData === 'object' && errorData.message
            ? errorData.message
            : `Error ${response.status}: ${response.statusText}`;

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  useEffect(() => {
    getAttention();
  }, [getAttention]);

  const openCase = (item) => {
    const path = `/review-kyc/${item.customerUserId}`;

    const navState = {
      component: 'dashboard',
      customerId: item.customerUserId,
      relationshipId: item.relationshipId,
      caseReference: item.sub,
      status: item.statusLabel,
    };

    const redirectState = {
      path,
      state: navState,
    };

    // Save to session
    sessionStorage.setItem('redirectAfterLogin', JSON.stringify(redirectState));

    // Navigate using the SAME computed path
    navigate(path, { state: redirectState.state });
  };

  return (
    <Card delay={60} className="p-5 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span
            className={`w-9 h-9 rounded-xl ${TONES.rose.tile} flex items-center justify-center transition-transform duration-300 hover:scale-110`}
          >
            <Flame size={17} strokeWidth={1.9} />
          </span>
          <div>
            <h2 className="font-semibold text-slate-900">
              Needs your attention
            </h2>
          </div>
        </div>

        {items.length > 0 && (
          <button
            type="button"
            onClick={() =>
              navigate('/customer-list', {
                state: { component: 'dashboard' },
              })
            }
            className="rounded-lg bg-brand px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-brand-dark hover:shadow-md active:scale-[0.97]"
          >
            View all
          </button>
        )}
      </div>

      {/* Loading skeleton — same row geometry, no layout shift */}
      {loading && (
        <div className="space-y-2.5">
          {Array.from({ length: Math.min(limit, 3) }).map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className="relative rounded-xl border border-slate-200/80 bg-white overflow-hidden"
            >
              <span className="absolute left-0 top-0 bottom-0 w-1 bg-slate-200" />
              <div className="pl-5 pr-4 py-3.5 flex items-center gap-3 lg:gap-5 animate-pulse">
                <div className="flex items-center gap-3 min-w-0 lg:w-60 shrink-0">
                  <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0" />
                  <div className="min-w-0 w-full space-y-1.5">
                    <div className="h-3 w-28 rounded bg-slate-200" />
                    <div className="h-2 w-20 rounded bg-slate-100" />
                  </div>
                </div>
                <div className="flex-1 h-3 rounded bg-slate-100" />
                <div className="w-24 h-3 rounded bg-slate-100 shrink-0" />
                <div className="w-20 h-8 rounded-lg bg-slate-200 shrink-0" />
                <div className="w-4 h-4 rounded bg-slate-100 shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center">
          <span className="mx-auto mb-2 w-9 h-9 rounded-xl bg-[#ECFDF5] text-[#059669] flex items-center justify-center">
            <CheckCircle2 size={17} strokeWidth={1.9} />
          </span>
          <p className="text-sm font-semibold text-slate-700">
            Nothing waiting
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            New submissions will show up here.
          </p>
        </div>
      )}

      {/* Case rows */}
      {!loading && items.length > 0 && (
        <div className="space-y-2.5">
          {items.map((item, i) => {
            const p = PRIORITY[item.priority];
            const isOpen = expanded === item.id;
            return (
              <div
                key={item.id}
                className={`animate-fade-up relative rounded-xl border bg-white transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? 'border-brand/30 shadow-lift ring-1 ring-brand/10'
                    : 'border-slate-200/80 hover:border-slate-300 hover:shadow-card hover:-translate-y-0.5'
                }`}
                style={{ animationDelay: `${120 + i * 60}ms` }}
              >
                {/* Priority accent bar */}
                <span
                  className="absolute left-0 top-0 bottom-0 w-1"
                  style={{ backgroundColor: p.accent }}
                />

                {/* Row */}
                <div
                  onClick={() => setExpanded(isOpen ? null : item.id)}
                  className="group cursor-pointer pl-5 pr-4 py-3.5 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5"
                >
                  {/* Identity */}
                  <div className="flex items-center gap-3 min-w-0 lg:w-60 shrink-0">
                    <div
                      className={`w-9 h-9 rounded-full ${avatarStyle(
                        item.name,
                      )} flex items-center justify-center text-xs font-semibold transition-transform duration-200 group-hover:scale-110 shrink-0`}
                    >
                      {initials(item.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {item.name}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {item.sub}
                      </p>
                    </div>
                  </div>

                  {/* Note */}
                  <p className="flex-1 text-sm text-slate-600 min-w-0 truncate">
                    {item.note}
                  </p>

                  {/* Due + review + chevron */}
                  <div className="flex items-center justify-between lg:justify-end gap-4 shrink-0">
                    <div className="w-24">
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-bold tabular-nums ${
                          DUE_TEXT[item.dueTone]
                        }`}
                      >
                        <Clock size={12} />
                        {item.due}
                      </span>
                      {/* SLA impact micro-bar */}
                      <div className="h-1 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full bar-anim ${
                            SLA_BAR[item.dueTone]
                          }`}
                          style={{
                            width: `${item.sla}%`,
                            animationDelay: `${300 + i * 80}ms`,
                          }}
                        />
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openCase(item);
                      }}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 active:scale-[0.97] bg-brand text-white shadow-sm hover:bg-brand-dark hover:shadow-[0_10px_20px_-10px_rgba(55,90,231,0.7)]"
                    >
                      <Eye size={13} />
                      {item.actionLabel}
                    </button>

                    <ChevronDown
                      size={16}
                      className={`text-slate-400 transition-transform duration-300 ${
                        isOpen ? 'rotate-180 text-brand' : ''
                      }`}
                    />
                  </div>
                </div>

                {/* Expandable detail */}
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="mx-4 mb-4 pl-1 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-start gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-[#F5F3FF] text-[#7C3AED] flex items-center justify-center shrink-0">
                          <CalendarDays size={13} />
                        </span>
                        <div>
                          <p className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase">
                            Submitted at
                          </p>
                          <p className="text-xs font-semibold text-slate-700 mt-0.5 tabular-nums">
                            {formatSubmitted(item.opened)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-[#ECFDF5] text-[#059669] flex items-center justify-center shrink-0">
                          <History size={13} />
                        </span>
                        <div>
                          <p className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase">
                            Last activity
                          </p>
                          <p className="text-xs font-semibold text-slate-700 mt-0.5">
                            {item.last}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
